import { JSONSchema7 } from 'json-schema';
import { useContext, useMemo } from 'react';
import {
  WorkflowEngineContext,
  WorkflowEngineMode
} from '../../WorkflowEngine/workflowEngineReducer';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import PageState from '../../Shared/pageState';
import Step from '@mui/material/Step';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import { PagePermissionLevel } from '../../Shared/Enums/pagePermissionLevel';
import getPagePermissionForRole from '../../Shared/jsonSchemaUtilities/getPagePermissionForRole';
import SideStepLabel from './SideStepLabel';
import SideStepper from './SideStepper';
import PageValidState from '../../Shared/pageValidState';

export default ({
  sx,
  handleStepSelection
}: {
  sx?: SxProps<Theme>;
  handleStepSelection: (index: number) => void;
}) => {
  const { state } = useContext(WorkflowEngineContext);
  const { pages, currentPage, role, mode, pagesValidState } = state;
  /**
   * Gets the current active page index.
   * This accounts for pages removed by role permissions rather than using the currentPage index.
   * Note: clinic user and provider can view all pages regardless of access.
   * @returns number
   */
  const getCurrentActiveIndex = () => {
    const availablePageList: PageState[] = [];
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      const pageSchema = (page.schema.properties as JSONSchema7Properties)[
        'page'
      ] as JSONSchema7;
      const hasAccess =
        mode == WorkflowEngineMode.ENTRY || mode == WorkflowEngineMode.PREVIEW
          ? getPagePermissionForRole(pageSchema, role) !==
            PagePermissionLevel.NoAccess
          : true;
      if ((role === 'Donor' && hasAccess) || role !== 'Donor') {
        availablePageList.push(pages[i]);
      }
    }
    return availablePageList.findIndex(
      (page) => page.id === pages[currentPage].id
    );
  };

  const renderStep = (
    page: PageState,
    index: number,
    pageValidState: PageValidState
  ) => {
    const pageSchema = (page.schema.properties as JSONSchema7Properties)[
      'page'
    ] as JSONSchema7;

    const labelSchema = (pageSchema.properties as JSONSchema7Properties)[
      'label'
    ] as JSONSchema7;

    const hasAccess =
      mode == WorkflowEngineMode.ENTRY || mode == WorkflowEngineMode.PREVIEW
        ? getPagePermissionForRole(pageSchema, role) !==
          PagePermissionLevel.NoAccess
        : true;
    const isPageReadOnly =
      getPagePermissionForRole(pageSchema, role) ===
      PagePermissionLevel.ReadOnly;
    const handleStepClick = () => {
      if (!hasAccess || index === currentPage) return;
      handleStepSelection(index);
    };
    const validState = pageValidState?.valid;
    const completed = validState ? validState : undefined;
    return role === 'Donor' ? (
      hasAccess ? (
        <Step key={page.id}>
          <Stack
            onClick={handleStepClick}
            sx={{
              cursor: hasAccess ? 'pointer' : 'default'
            }}
          >
            <SideStepLabel
              completed={completed}
              error={validState === false}
              label={labelSchema.default as string}
              readOnly={isPageReadOnly}
            />
          </Stack>
        </Step>
      ) : null
    ) : (
      <Step key={page.id}>
        <Stack
          onClick={handleStepClick}
          sx={{
            cursor: hasAccess ? 'pointer' : 'default'
          }}
        >
          <SideStepLabel
            completed={completed}
            error={validState === false}
            label={labelSchema.default as string}
            readOnly={isPageReadOnly}
          />
        </Stack>
      </Step>
    );
  };

  return useMemo(
    () =>
      pages.length ? (
        <Stack
          sx={{
            '.MuiBox-root': {
              maxWidth: '100%',
              '.MuiStepper-root.MuiStepper-vertical': {
                paddingRight: '20px'
              }
            },
            ...sx
          }}
        >
          <SideStepper activeStep={getCurrentActiveIndex()}>
            {pages.map((page: PageState, index: number) =>
              renderStep(page, index, pagesValidState[index])
            )}
          </SideStepper>
        </Stack>
      ) : null,
    [pages, currentPage, role, pagesValidState]
  );
};
